/**
 * useApprovalQueue — den samlede godkjenningskøen (#155, redesign steg 5a).
 *
 * Godkjenn-skjermen viser tre køer som én liste, eldste først:
 *   - oppgaver som venter   GET /api/chores/awaiting-approval   (nøkkel ['approvals'])
 *   - forslag fra barna      GET /api/chores/proposals           (['chore-proposals'])
 *   - belønningsbytter       GET /api/rewards/claims/pending     (['reward-claims', 'pending'])
 *
 * Nøklene er de samme som før, så WS-invalideringene i useChoresWS og
 * usePointsWS oppdaterer køen og tellemerket i menyen uten ny kobling.
 * Alle tre endepunktene er bare for voksne; `enabled` styrer hentingen.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApprovalInstance } from './ApprovalCard';
import { useProposals } from './useProposals';
import { usePendingRewardClaims, type RewardClaimRow } from '../rewards/useRewardsQueries';
import type { ChoreTemplateRow } from '../admin/useChoreTemplates';

export const APPROVALS_KEY = ['approvals'] as const;

const HOUR_MS = 3600_000;

export type QueueKind = 'chore' | 'proposal' | 'reward';

export type QueueItem =
  | { kind: 'chore'; id: string; at: string | null; chore: ApprovalInstance }
  | { kind: 'proposal'; id: string; at: string | null; proposal: ChoreTemplateRow }
  | { kind: 'reward'; id: string; at: string | null; claim: RewardClaimRow };

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const e = new Error(`fetch_failed:${r.status}`) as Error & { status?: number };
    e.status = r.status;
    throw e;
  }
  return (await r.json()) as T;
}

export function useAwaitingApprovals(enabled: boolean) {
  return useQuery<ApprovalInstance[]>({
    queryKey: APPROVALS_KEY,
    queryFn: () => fetchJson<ApprovalInstance[]>('/api/chores/awaiting-approval'),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** Et svar som ikke er en liste (feilside, gammel klient) teller som tomt. */
function listOf<T>(data: T[] | undefined): T[] {
  return Array.isArray(data) ? data : [];
}

function timeOf(iso: string | null): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Hele timer elementet har ventet, eller null uten tidspunkt. */
export function waitingHours(at: string | null, now: Date): number | null {
  if (!at) return null;
  const ms = now.getTime() - new Date(at).getTime();
  return Number.isNaN(ms) ? null : Math.max(0, Math.floor(ms / HOUR_MS));
}

/**
 * De tre køene flettet til én liste, eldste først. Et element uten tidspunkt
 * havner sist; likt tidspunkt beholder rekkefølgen oppgaver → forslag → bytter.
 */
export function useApprovalQueue(enabled: boolean): {
  items: QueueItem[];
  isLoading: boolean;
} {
  const chores = useAwaitingApprovals(enabled);
  const proposals = useProposals(enabled);
  const claims = usePendingRewardClaims(enabled);

  const items = useMemo(() => {
    const all: QueueItem[] = [
      ...listOf(chores.data).map(
        (chore): QueueItem => ({ kind: 'chore', id: chore.id, at: chore.completed_at, chore }),
      ),
      ...listOf(proposals.data).map(
        (proposal): QueueItem => ({
          kind: 'proposal',
          id: proposal.id,
          at: proposal.created_at ?? null,
          proposal,
        }),
      ),
      ...listOf(claims.data).map(
        (claim): QueueItem => ({ kind: 'reward', id: claim.id, at: claim.claimed_at, claim }),
      ),
    ];
    // Array.prototype.sort er stabil, så likt tidspunkt beholder rekkefølgen over.
    return all.sort((a, b) => timeOf(a.at) - timeOf(b.at));
  }, [chores.data, proposals.data, claims.data]);

  return { items, isLoading: chores.isLoading };
}

/** Antall i alle tre køene, for merket på Godkjenn i menyen. */
export function usePendingApprovalCount(enabled: boolean): number {
  const { items } = useApprovalQueue(enabled);
  return enabled ? items.length : 0;
}
